/* Feb-2026 · Bank Reconciliation
 *
 * Upload a Stripe-payout or bank-statement CSV. The backend proposes
 * matches against pending AR ledger rows using ±$0.01 amount and ±3-day
 * tolerance. Bookkeeper reviews the matches, deselects any bad ones,
 * then clicks "Apply" to clear the matched rows.
 * Backend: POST /api/admin/gl/reconcile/import-csv, /apply
 */
import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { PanelHeader } from './_shared';
import { Upload, Loader2, FileText, CheckCircle2, AlertTriangle, Landmark } from 'lucide-react';
import { LedgerAccessLink } from './LedgerAccessLink';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const fmtUsd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function BankReconciliationPanel() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});   // csv_row_index -> bool
  const [applying, setApplying] = useState(false);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error('Choose a CSV first.'); return; }
    const fd = new FormData();
    fd.append('file', f);
    setBusy(true);
    try {
      const r = await http.post('/admin/gl/reconcile/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      // Auto-select every proposed match
      const sel = {};
      (r.data.matches || []).forEach((m) => { sel[m.csv_row_index] = true; });
      setSelected(sel);
      toast.success(`Parsed ${r.data.parsed_rows} rows · ${r.data.matches.length} auto-matched`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed.');
    } finally { setBusy(false); }
  };

  const apply = async () => {
    const chosen = (result?.matches || []).filter((m) => selected[m.csv_row_index]);
    if (chosen.length === 0) { toast.error('Select at least one match.'); return; }
    setApplying(true);
    try {
      const r = await http.post('/admin/gl/reconcile/apply', { matches: chosen });
      toast.success(`Cleared ${r.data.cleared_count} ledger row(s).`);
      setResult(null);
      setSelected({});
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed.');
    } finally { setApplying(false); }
  };

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected],
  );

  return (
    <div className="space-y-4" data-testid="bank-recon-panel">
      <PanelHeader
        icon={Landmark}
        title="Bank Reconciliation"
        subtitle="Upload a Stripe-payout or bank-statement CSV. Review the proposed Cash matches, then clear them in one click."
      />
      <div className="flex justify-end"><LedgerAccessLink testId="bank-recon-open-ledger" /></div>

      <div className="rounded-md border border-border bg-card p-4" data-testid="bank-recon-upload">
        <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          1 · Upload CSV
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-primary hover:file:bg-primary/20"
            data-testid="bank-recon-file"
          />
          <Button size="sm" onClick={upload} disabled={busy} data-testid="bank-recon-upload-btn">
            {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-2 h-3.5 w-3.5" />}
            Analyse
          </Button>
        </div>
        <div className="mt-2 text-[11px] text-muted-foreground">
          Expected columns (case-insensitive): <span className="font-mono">date, amount, description, reference</span>.
          Stripe payout CSVs work out of the box.
        </div>
      </div>

      {result && (
        <>
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="bank-recon-matches">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-emerald-900">
                2 · Proposed matches · {result.matches.length}
              </div>
              <Button size="sm" onClick={apply} disabled={applying || selectedCount === 0} data-testid="bank-recon-apply-btn">
                {applying ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-2 h-3.5 w-3.5" />}
                Apply {selectedCount ? `(${selectedCount})` : ''}
              </Button>
            </div>
            {result.matches.length === 0 && (
              <div className="text-sm text-muted-foreground">No matches found. Try widening the date range or verify amounts.</div>
            )}
            {result.matches.length > 0 && (
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wider text-emerald-900/70">
                  <tr>
                    <th className="px-2 py-1.5 text-left">✓</th>
                    <th className="px-2 py-1.5 text-left">CSV Date</th>
                    <th className="px-2 py-1.5 text-right">CSV Amt</th>
                    <th className="px-2 py-1.5 text-left">Description</th>
                    <th className="px-2 py-1.5 text-left">→ Ledger Party</th>
                    <th className="px-2 py-1.5 text-right">Ledger Amt</th>
                  </tr>
                </thead>
                <tbody>
                  {result.matches.map((m) => (
                    <tr key={m.csv_row_index} className="border-t border-emerald-200/40" data-testid={`bank-recon-match-${m.csv_row_index}`}>
                      <td className="px-2 py-1.5">
                        <Checkbox
                          checked={!!selected[m.csv_row_index]}
                          onCheckedChange={(v) => setSelected((s) => ({ ...s, [m.csv_row_index]: !!v }))}
                          data-testid={`bank-recon-check-${m.csv_row_index}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">{m.csv_date}</td>
                      <td className="px-2 py-1.5 text-right font-mono-stat">{fmtUsd(m.csv_amount_usd)}</td>
                      <td className="px-2 py-1.5">{m.csv_description || '—'}</td>
                      <td className="px-2 py-1.5">{m.ledger_party}</td>
                      <td className="px-2 py-1.5 text-right font-mono-stat">{fmtUsd(m.ledger_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {result.unmatched_csv.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="bank-recon-unmatched-csv">
              <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-amber-900">
                3 · Unmatched CSV rows · {result.unmatched_csv.length}
              </div>
              <div className="max-h-52 overflow-y-auto">
                <table className="w-full text-[11.5px]">
                  <thead className="text-[10px] uppercase text-amber-900/70">
                    <tr>
                      <th className="px-2 py-1 text-left">Date</th>
                      <th className="px-2 py-1 text-right">Amount</th>
                      <th className="px-2 py-1 text-left">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.unmatched_csv.slice(0, 50).map((r) => (
                      <tr key={r.index} className="border-t border-amber-200/40">
                        <td className="px-2 py-1">{r.date}</td>
                        <td className="px-2 py-1 text-right font-mono-stat">{fmtUsd(r.amount_usd)}</td>
                        <td className="px-2 py-1">{r.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-[11px] text-amber-900/70">
                Post these as new AR entries via the Ledger panel and re-run the import.
              </div>
            </div>
          )}

          {result.unmatched_ledger.length > 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-700">
                Unmatched pending AR · {result.unmatched_ledger.length}
              </div>
              <div className="text-[11.5px] text-slate-600">
                These pending AR rows had no CSV counterpart. They will remain pending after apply.
              </div>
            </div>
          )}

          {result.csv_errors.length > 0 && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-4">
              <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-rose-900">
                <AlertTriangle className="h-3.5 w-3.5" /> CSV errors · {result.csv_errors.length}
              </div>
              <ul className="text-[11.5px]">
                {result.csv_errors.slice(0, 10).map((e) => (
                  <li key={e.index}>Row {e.index + 1}: {e.error}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
